import { useState } from "react";
import BusinessSummary from "../../components/BusinessSummary";
import BusinessForm from "../../components/BusinessForm";
import LeadCaptureModal from "../../components/LeadCaptureModal";
import { runBusinessAssessment } from "../../api/diagnosticsApi";
import { Loader2, CheckCircle2, Zap, Mail, Briefcase, Settings, TrendingUp, Laptop } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import strivreLogo from "../../assets/images/strivre-logo.svg";

export default function BusinessAssessmentTool() {
  const [report, setReport] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [requiresLeadCapture, setRequiresLeadCapture] = useState(false);
  const [pendingAnswers, setPendingAnswers] = useState<Record<string, string>>({});
  const [, setLeadCaptureId] = useState<string | null>(null);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [capturedEmail, setCapturedEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const { token } = useAuth();
  const isAuthenticated = !!token;
  const pageWrapperClass = isAuthenticated
  ? "min-h-screen pt-10 p-6 md:p-8 lg:p-10 md:ml-0"
  : "min-h-screen px-4 sm:px-6 lg:px-8 text-slate-100";

  async function runDiagnostic(
    answers: Record<string, string>,
    captchaToken: string | null
  ) {
    setIsLoading(true);
    setError(null);
    setPendingAnswers(answers);

    try {
      const data = await runBusinessAssessment({
        answers,
        ...(captchaToken ? { captchaToken } : {})
      });

      if (data.requiresLeadCapture) {
        setRequiresLeadCapture(true);
        setReport(null);
        setLeadCaptured(false);
        return;
      }

      setReport(data.report ?? data);
      setRequiresLeadCapture(false);
      setLeadCaptured(false);
      setLeadCaptureId(data.leadCaptureId ?? null);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to run diagnostics. Please try again.";

      console.error(
        "Error running business diagnostic:",
        errorMessage
      );

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleLeadCaptureSubmit(data: { name: string; company: string; email: string }, captchaToken: string) {
    setIsLoading(true);
    try {
      const response = await runBusinessAssessment({ answers: pendingAnswers, name: data.name, email: data.email, companyName: data.company, captchaToken });
      if (response.report) {
        setReport(response.report);
        setRequiresLeadCapture(false);
        setLeadCaptured(true);
        setLeadCaptureId(response.leadCaptureId ?? null);
        setCapturedEmail(data.email);
      } else {
        throw new Error("No report was returned after lead capture.");
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to submit lead capture. Please try again.";
      console.error("Error submitting lead capture:", errorMessage);
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }

  function resetState() {
    setRequiresLeadCapture(false);
    setLeadCaptured(false);
    setCapturedEmail(null);
    setLeadCaptureId(null);
    setReport(null);
    setError(null);
    setPendingAnswers({});
  }

  return (
    <div className={pageWrapperClass}>
      <LeadCaptureModal
        isOpen={requiresLeadCapture}
        onClose={resetState}
        onSubmit={handleLeadCaptureSubmit}
        loading={isLoading}
        websiteUrl="your business assessment"
        title="Get Your Business Assessment Report"
        description="We need a few details so we can send your assessment report and follow up with tailored recommendations."
        companyLabel="Company"
      />

      {!isAuthenticated ? (
        <header className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen bg-strivre-navy text-white border-b border-strivre-lightnavy shadow-lg mb-12">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">

            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="bg-white rounded-xl px-2">
                <img
                  src={strivreLogo}
                  alt="Strivre"
                  className="h-16 w-auto object-contain"
                  />
              </div>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/diagnostics/website-tool" className="text-white/70 hover:text-white transition-colors">Web Diagnostics</Link>
              <Link to="/diagnostics/marketing-tool" className="text-white/70 hover:text-white transition-colors">Marketing Diagnostics</Link>
              <Link to="/diagnostics/business-assessment" className="text-white font-semibold">Business Diagnostics</Link>
              <Link to="/diagnostics/talent-diagnostics" className="text-white/70 hover:text-white transition-colors">Talent Diagnostics</Link>
              <Link to="/diagnostics/recruitment-diagnostics" className="text-white/70 hover:text-white transition-colors">Recruitment Diagnostics</Link>
            </nav>
            <div className="flex items-center gap-3">
                <button
                  className="btn-primary btn-md shrink-0"
                  onClick={() => navigate("/login")}
                >
                  Login
                </button>

                {/* <button
                  className="btn-secondary btn-md shrink-0"
                  onClick={() => navigate("/register")}
                >
                  Sign Up
                </button> */}
            </div>
          </div>
        </header>
      ) : (
        <header className="mt-5 mb-8 flex items-center justify-between animate-fade-in">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 leading-tight mb-0">
              <Zap className="text-strivre-burgundy" size={24} />
              {report && !isLoading
                ? "Assessment Results"
                : "Business Assessment"}
            </h1>

            <p
              className={`text-slate-500 text-sm mt-1 ${
                !report || isLoading ? "text-center" : "text-left"
              }`}
            >
              {report && !isLoading
                ? "Review your personalized business health assessment below."
                : "Answer a few questions to assess your business operations, marketing, technology, and growth readiness."}
            </p>
          </div>
        </header>
      )}

      {!report && !leadCaptured && (
        <div className={!isAuthenticated ? "max-w-7xl mx-auto mb-8 animate-fade-in" : "w-full mb-8 animate-fade-in"}>
          <div className="rounded-3xl border border-strivre-lightnavy bg-white p-8 shadow-sm">

            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 rounded-full bg-strivre-lightburgundy px-4 py-2 text-sm font-semibold text-strivre-burgundy mb-4">
                <Briefcase size={16} />
                Free Business Assessment
              </div>

              <h2 className="text-4xl font-bold text-slate-900 mb-4">
                How Healthy Is Your Business?
              </h2>

              <p className="text-lg text-slate-600">
                Gain an objective view of your business operations, growth readiness,
                and strategic direction. Discover strengths, uncover risks, and
                identify opportunities for sustainable growth.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mt-8">
              <div className="rounded-2xl border border-slate-200 p-5">
                <Settings className="text-strivre-burgundy mb-3" size={22} />
                <h3 className="font-semibold text-slate-900 mb-2">
                  Operations
                </h3>
                <p className="text-sm text-slate-600">
                  Assess operational efficiency, consistency, and day-to-day execution.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 p-5">
                <TrendingUp className="text-strivre-burgundy mb-3" size={22} />
                <h3 className="font-semibold text-slate-900 mb-2">
                  Growth Readiness
                </h3>
                <p className="text-sm text-slate-600">
                  Evaluate your ability to scale, adapt, and achieve future goals.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 p-5">
                <Laptop className="text-strivre-burgundy mb-3" size={22} />
                <h3 className="font-semibold text-slate-900 mb-2">
                  Technology & Strategy
                </h3>
                <p className="text-sm text-slate-600">
                  Review how technology and planning support business performance.
                </p>
              </div>
            </div>

          </div>
        </div>
      )}

      {!report && !isLoading && !requiresLeadCapture && (
        <div className={!isAuthenticated ? "max-w-7xl mx-auto mb-8 animate-fade-in" : "w-full mb-8 animate-fade-in"}>
          {error && (
            <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          <BusinessForm onSubmit={runDiagnostic} isAuthenticated={isAuthenticated} />
        </div>
      )}

      {isLoading && (
        <div className="flex flex-col items-center justify-center py-20 animate-pulse">
          <Loader2 className="w-12 h-12 text-strivre-burgundy animate-spin mb-4" />
          <p className="text-slate-500 font-medium text-lg">Analyzing your business structure...</p>
        </div>
      )}

      {report && !isLoading && !requiresLeadCapture && (
        <>
          {!isAuthenticated && leadCaptured ? (
            <div className="max-w-2xl mx-auto mb-12 animate-fade-in">
              <div className="bg-gradient-to-br from-strivre-softgrey to-white rounded-3xl border-2 border-strivre-lightnavy p-12 text-center shadow-lg">

                <div className="mb-6 flex justify-center">
                  <div className="relative">
                    <div className="w-20 h-20 bg-strivre-burgundy rounded-full flex items-center justify-center">
                      <CheckCircle2 size={40} className="text-white" />
                    </div>

                    <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-strivre-navy rounded-full flex items-center justify-center border-4 border-white">
                      <Mail size={16} className="text-white" />
                    </div>
                  </div>
                </div>

                <h2 className="text-3xl font-bold text-slate-900 mb-3">
                  Assessment Sent to Your Email!
                </h2>

                <p className="text-slate-700 text-lg mb-2">
                  We've sent your detailed business assessment to:
                </p>

                <p className="text-strivre-burgundy font-semibold text-lg mb-8 break-all">
                  {capturedEmail}
                </p>

                <div className="space-y-3 text-left text-slate-700 mb-8 max-w-md mx-auto">

                  <div className="flex items-start gap-3">
                    <CheckCircle2
                      size={20}
                      className="text-strivre-burgundy flex-shrink-0 mt-1"
                    />
                    <p>
                      Check your inbox for your complete business health report
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle2
                      size={20}
                      className="text-strivre-burgundy flex-shrink-0 mt-1"
                    />
                    <p>
                      The report contains recommendations to improve your operations,
                      marketing, digital strategy and technology
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle2
                      size={20}
                      className="text-strivre-burgundy flex-shrink-0 mt-1"
                    />
                    <p>
                      Our consultants may contact you to discuss growth opportunities
                    </p>
                  </div>

                </div>

                <p className="text-sm text-slate-600 mb-6">
                  Didn't receive the email? Check your spam folder or{" "}
                  <button
                    onClick={resetState}
                    className="text-strivre-burgundy hover:text-red-900 font-semibold underline"
                  >
                    try another email
                  </button>
                </p>

                <button
                  onClick={resetState}
                  className="inline-block px-8 py-3 bg-strivre-burgundy text-white font-bold rounded-full hover:bg-red-900 transition-colors"
                >
                  Run Another Assessment
                </button>

              </div>
            </div>
          ) : (
            <div className="mt-8">
              <BusinessSummary
                overallPercentage={report.overallPercentage}
                visualIndicator={report.visualIndicator || report.grade}
                categoryScores={report.categoryScores}
                report={report.summaryReport}
                recommendations={report.recommendations}
                services={report.ctaServices}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}